import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, setToken } from '../lib/api';
import { BrandLogo } from '../components/BrandLogo';

// Turnstile applies only to public (proxied) access; LAN-direct is trusted.
const IS_PUBLIC = !/^(localhost|127\.|192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(window.location.hostname);

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [tsToken, setTsToken] = useState('');
  const [siteKey, setSiteKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Turnstile is per-instance: only enforced when the backend supplies a site key (#157).
  const needsTs = IS_PUBLIC && !!siteKey;

  // OIDC callback support: token in URL -> store and redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oidcToken = params.get('oidc_token');
    if (oidcToken) {
      setToken(oidcToken);
      window.history.replaceState({}, '', '/');
      navigate('/');
    }
  }, [navigate]);

  // Fetch this instance's Turnstile site key (#157). Empty -> Turnstile disabled for this instance.
  useEffect(() => {
    if (!IS_PUBLIC) return;
    fetch('/api/auth/config')
      .then(r => r.json())
      .then(c => setSiteKey(c.turnstile_site_key || ''))
      .catch(() => {});
  }, []);

  // Load + render Cloudflare Turnstile (public access + a configured site key only)
  useEffect(() => {
    if (!needsTs) return;
    function render() {
      const w: any = window;
      const el = document.getElementById('ts-widget');
      if (w.turnstile && el && !el.hasChildNodes()) {
        w.turnstile.render('#ts-widget', {
          sitekey: siteKey,
          callback: (t: string) => setTsToken(t),
          'expired-callback': () => setTsToken(''),
          'error-callback': () => setTsToken(''),
        });
      }
    }
    const w: any = window;
    if (w.turnstile) { render(); return; }
    w.__tsonload = render;
    if (!document.getElementById('cf-turnstile-script')) {
      const s = document.createElement('script');
      s.id = 'cf-turnstile-script';
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__tsonload';
      s.async = true; s.defer = true;
      document.head.appendChild(s);
    }
  }, [needsTs, siteKey]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (needsTs && !tsToken) { setError('Please complete the verification'); return; }
    setLoading(true); setError('');
    try {
      const r = await api.login(username, password, tsToken);
      setToken(r.token);
      navigate('/');
    } catch (e: any) {
      setError(e.message || 'login failed');
      const w: any = window;
      if (needsTs && w.turnstile) w.turnstile.reset('#ts-widget');
      setTsToken('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-96 space-y-4">
        <div className="flex justify-center"><BrandLogo size={56} iconOnly /></div>
        <div className="text-center">
          <div className="text-2xl font-semibold tracking-tight">Stratus VoIP</div>
          <div className="text-xs uppercase tracking-widest text-textSecondary">Cloud-native PBX</div>
        </div>
        <div className="card">
          <h1 className="text-base font-medium mb-4 text-center">Sign in</h1>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">Username</label>
            <input className="input" value={username} onChange={e => setUsername(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Password</label>
            <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          {needsTs && <div id="ts-widget" className="flex justify-center"></div>}
          {error && <div className="text-danger text-sm">{error}</div>}
          <button type="submit" disabled={loading || (needsTs && !tsToken)} className="btn-primary w-full">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className="mt-4 pt-4 border-t border-border text-center text-sm">
          <a href="/api/auth/oidc/m365/start?domain_uuid=" className="text-primary hover:underline">Sign in with Microsoft 365</a>
          <span className="mx-2 text-textSecondary">·</span>
          <a href="/api/auth/oidc/google/start?domain_uuid=" className="text-primary hover:underline">Sign in with Google</a>
        </div>
        </div>
      </div>
    </div>
  );
}
